/**
 * Bons d'achat de matériaux : ce que l'écran du bureau et l'espace de
 * l'employé en disent.
 *
 * <p>Le bureau (patron, technicien, secrétaire, comptable) envoie un employé
 * chercher du matériel chez un fournisseur, pour un chantier en cours. Le bon
 * ne porte aucun prix : le fournisseur facture la société avec le numéro du
 * bon et le chantier.
 */

/** Les statuts du serveur, dans les mots de l'écran. */
export const STATUTS_BON = {
  ETABLI: 'Pas encore envoyé',
  ENVOYE: 'À acheter',
  VU: 'À acheter',
  ACHETE: 'Acheté ✓',
};

/**
 * @param {string} statut statut du serveur
 * @returns {boolean} vrai tant que l'achat reste à faire
 */
export const resteAAcheter = (statut) => statut === 'ENVOYE' || statut === 'VU';

/** Une ligne vide du formulaire. */
export const ligneVide = () => ({ designation: '', quantite: '', unite: 'pce' });

/**
 * Un bon neuf, prêt à remplir.
 *
 * @param {object} [depart] chantier ou employé déjà choisis
 * @returns {object} le bon
 */
export const bonVide = (depart = {}) => ({
  chantierId: depart.chantierId ?? '',
  employeId: depart.employeId ?? '',
  fournisseur: '',
  fournisseurAdresse: '',
  lignes: [ligneVide()],
  remarque: '',
});

/**
 * Les lignes à enregistrer : sans les lignes vides, la quantité en nombre.
 *
 * @param {object[]} lignes lignes du formulaire
 * @returns {{designation: string, quantite: number|null, unite: string|null}[]} les lignes
 */
export function lignesPropres(lignes) {
  return (lignes || [])
    .map((l) => {
      const q = String(l.quantite ?? '')
        .trim()
        .replace(',', '.');
      return {
        designation: String(l.designation || '').trim(),
        quantite: q === '' || Number.isNaN(Number(q)) ? null : Number(q),
        unite: String(l.unite || '').trim() || null,
      };
    })
    .filter((l) => l.designation);
}

/**
 * Ce qui manque avant d'enregistrer, en clair ; null si le bon est complet.
 * Au magasin, le bon remplit le stock : il n'a pas de chantier.
 *
 * @param {object} bon bon du formulaire
 * @param {boolean} [magasin] vrai pour un bon du magasin
 * @returns {string|null} le message
 */
export function manqueAuBon(bon, magasin = false) {
  if (!bon) return 'Choisissez le chantier.';
  if (!magasin && !bon.chantierId) return 'Choisissez le chantier.';
  if (!bon.employeId) return 'Choisissez l’employé qui va acheter.';
  if (!String(bon.fournisseur || '').trim()) return 'Indiquez le fournisseur.';
  if (!lignesPropres(bon.lignes).length) return 'Ajoutez au moins un matériau.';
  return null;
}

/**
 * Le bon tel que le serveur l'attend.
 *
 * @param {object} bon bon du formulaire
 * @returns {object} le corps de la requête
 */
export const bonVersDto = (bon) => ({
  dateBon: bon.dateBon || null,
  chantierId: bon.chantierId ? Number(bon.chantierId) : null,
  employeId: Number(bon.employeId),
  fournisseur: String(bon.fournisseur || '').trim(),
  fournisseurAdresse: String(bon.fournisseurAdresse || '').trim() || null,
  lignes: lignesPropres(bon.lignes),
  remarque: String(bon.remarque || '').trim() || null,
});

/**
 * Le bon enregistré, repris dans le formulaire pour le modifier.
 *
 * @param {object} dto bon du serveur
 * @returns {object} le bon du formulaire
 */
export const dtoVersBon = (dto) => ({
  ...dto,
  chantierId: dto.chantierId ?? '',
  employeId: dto.employeId ?? '',
  fournisseurAdresse: dto.fournisseurAdresse || '',
  remarque: dto.remarque || '',
  lignes: (dto.lignes || []).length
    ? dto.lignes.map((l) => ({
        designation: l.designation || '',
        quantite: l.quantite == null ? '' : String(l.quantite),
        unite: l.unite || '',
      }))
    : [ligneVide()],
});

/**
 * Les fournisseurs déjà utilisés, du plus fréquent au moins fréquent, avec
 * leur dernière adresse : un toucher remplit le bon suivant.
 *
 * @param {object[]} bons bons existants
 * @returns {{nom: string, adresse: string}[]} les fournisseurs
 */
export function fournisseursConnus(bons) {
  const parNom = new Map();
  (bons || []).forEach((b) => {
    const nom = String(b.fournisseur || '').trim();
    if (!nom) return;
    const cle = nom.toLowerCase();
    const avant = parNom.get(cle) || { nom, adresse: '', fois: 0 };
    parNom.set(cle, {
      nom: avant.nom,
      adresse: avant.adresse || b.fournisseurAdresse || '',
      fois: avant.fois + 1,
    });
  });
  return [...parNom.values()]
    .sort((a, b) => b.fois - a.fois || a.nom.localeCompare(b.nom))
    .map(({ nom, adresse }) => ({ nom, adresse }));
}

/**
 * Les chantiers où l'on peut envoyer acheter : ceux en cours, d'abord.
 *
 * @param {object[]} chantiers chantiers de l'écran
 * @returns {object[]} les chantiers en cours
 */
export const chantiersEnCours = (chantiers) =>
  (chantiers || []).filter((c) => c.statut === 'En cours' || c.statut === 'EN_COURS');

/**
 * @param {object} e employé de l'écran
 * @returns {string} « Marc Dupont »
 */
export const nomEmploye = (e) => [e?.prenom, e?.nom].filter(Boolean).join(' ').trim();

/**
 * @param {object[]} bons bons de l'employé
 * @returns {number} ceux qu'il n'a pas encore ouverts
 */
export const nonOuverts = (bons) => (bons || []).filter((b) => b.statut === 'ENVOYE').length;

/**
 * « 12 pce » : la quantité et l'unité d'une ligne, sans « .0 ».
 *
 * @param {object} l ligne
 * @returns {string} la quantité lisible
 */
export function quantiteLisible(l) {
  const net = (v) => String(v).replace(/\.0+$/, '');
  const q = l?.quantite == null || l.quantite === '' ? '' : net(l.quantite);
  // Pris en partie au comptoir : « 2 / 5 sac » — ce qui a été pris, sur ce
  // qui était demandé.
  const pris = estPartiel(l) ? net(l.quantitePrise) + (q ? ' / ' + q : '') : q;
  return [pris, l?.unite || ''].filter(Boolean).join(' ');
}

/**
 * @param {object} l ligne du bon
 * @returns {boolean} vrai pour un matériau pris en partie (2 sacs sur 5)
 */
export const estPartiel = (l) =>
  l?.etat === 'PRIS' && l.quantitePrise != null && l.quantitePrise !== '';

/**
 * Les quantités prises à envoyer au serveur, dans l'ordre du bon : null
 * pour une ligne prise telle que demandée.
 *
 * @param {object[]} lignes lignes du bon
 * @param {Object<number, number>} prises quantités corrigées, par rang
 * @returns {Array<number|null>|null} les quantités, ou null si rien n'a changé
 */
export function quantitesPrises(lignes, prises) {
  const liste = (lignes || []).map((l, i) =>
    prises?.[i] == null || +prises[i] === +l.quantite ? null : +prises[i],
  );
  return liste.some((q) => q != null) ? liste : null;
}

/**
 * @param {object} bon bon du serveur
 * @returns {number} les matériaux en rupture chez le fournisseur (✗)
 */
export const nombreManquants = (bon) =>
  (bon?.lignes || []).filter((l) => l.etat === 'MANQUANT').length;

/**
 * Le statut tel que le bureau le lit : un bon soldé sans rien de pris dit
 * « Rien de disponible », un bon soldé avec des ✗ le dit aussi.
 *
 * @param {object} bon bon du serveur
 * @returns {string} le libellé
 */
export function statutLisible(bon) {
  if (bon?.statut !== 'ACHETE') return STATUTS_BON[bon?.statut] || bon?.statut || '';
  const n = nombreManquants(bon);
  if (n && n === (bon.lignes || []).length) return 'Rien de disponible';
  return n ? 'Acheté (' + n + ' ✗)' : STATUTS_BON.ACHETE;
}

/**
 * Le fournisseur du carnet qui porte ce nom (sans casse ni espaces).
 *
 * @param {object[]} carnet fournisseurs enregistrés
 * @param {string} nom nom tapé
 * @returns {object|undefined} le fournisseur
 */
export const fournisseurDuCarnet = (carnet, nom) => {
  const cle = String(nom || '')
    .trim()
    .toLowerCase();
  return cle
    ? (carnet || []).find(
        (f) =>
          String(f.nom || '')
            .trim()
            .toLowerCase() === cle,
      )
    : undefined;
};
